/**
 * Régua canvas — drag the steps, click to connect them.
 *
 * Written on plain pointer events rather than a drag-and-drop library. The
 * libraries in this space are built for lists and sortable grids; a flow graph
 * needs free positioning plus edges, and bending a sortable into that shape
 * costs more code than the forty lines below — and a dependency the panel would
 * carry forever.
 *
 * ## Interaction
 *
 *  - **Move**: drag the step's header. Positions snap to a 10px grid so a flow
 *    stays legible without anyone aligning anything by hand.
 *  - **Connect**: click an output port, then click the destination step. Esc
 *    cancels. Connecting by dragging a wire looks nicer and is much harder to
 *    hit on a trackpad — and impossible from a keyboard.
 *  - **Disconnect**: click the edge.
 *
 * Every step is also reachable by keyboard: the header is a button, Enter
 * selects, and the inspector on the right edits whatever is selected. An
 * operator who cannot use a mouse can still build the whole flow.
 */

import { rotuloEtapa, type Etapa, type Ramo } from '@egba-do/shared'
import { useCallback, useEffect, useRef, useState } from 'react'

const LARGURA = 220
const ALTURA_BASE = 64
const GRADE = 10

const CORES: Record<Etapa['tipo'], { borda: string; fundo: string; texto: string }> = {
  gatilho: { borda: 'border-emerald-300', fundo: 'bg-emerald-50', texto: 'text-emerald-900' },
  espera: { borda: 'border-slate-300', fundo: 'bg-slate-50', texto: 'text-slate-700' },
  condicao: { borda: 'border-amber-300', fundo: 'bg-amber-50', texto: 'text-amber-900' },
  acao: { borda: 'border-marca-300', fundo: 'bg-marca-50', texto: 'text-marca-900' },
  divisao_ab: { borda: 'border-violet-300', fundo: 'bg-violet-50', texto: 'text-violet-900' },
  fim: { borda: 'border-slate-300', fundo: 'bg-white', texto: 'text-slate-500' },
}

const NOMES: Record<Etapa['tipo'], string> = {
  gatilho: 'Gatilho',
  espera: 'Espera',
  condicao: 'Condição',
  acao: 'Ação',
  divisao_ab: 'Teste A/B',
  fim: 'Fim',
}

const RAMOS: Record<Etapa['tipo'], Ramo[]> = {
  gatilho: ['default'],
  espera: ['default'],
  acao: ['default'],
  condicao: ['sim', 'nao'],
  divisao_ab: ['a', 'b'],
  fim: [],
}

const ROTULO_RAMO: Record<Ramo, string> = {
  default: '→',
  sim: 'sim',
  nao: 'não',
  a: 'A',
  b: 'B',
}

function alturaDe(e: Etapa): number {
  return ALTURA_BASE + (RAMOS[e.tipo].length > 0 ? 22 : 0)
}

/** Bezier from the bottom of one step to the top of the next. */
function caminho(x1: number, y1: number, x2: number, y2: number): string {
  const dy = Math.max(30, Math.abs(y2 - y1) / 2)
  return `M ${x1} ${y1} C ${x1} ${y1 + dy}, ${x2} ${y2 - dy}, ${x2} ${y2}`
}

export interface CanvasProps {
  etapas: Etapa[]
  selecionada: string | null
  problemasPorEtapa: Map<string, string[]>
  somenteLeitura?: boolean
  onSelecionar: (id: string | null) => void
  onMover: (id: string, x: number, y: number) => void
  onConectar: (de: string, quando: Ramo, para: string) => void
  onDesconectar: (de: string, quando: Ramo) => void
}

export function Canvas({
  etapas,
  selecionada,
  problemasPorEtapa,
  somenteLeitura = false,
  onSelecionar,
  onMover,
  onConectar,
  onDesconectar,
}: CanvasProps) {
  const [ligando, setLigando] = useState<{ de: string; quando: Ramo } | null>(null)
  // 85% por padrão: uma régua de dez etapas cabe quase inteira na tela, e o
  // texto continua legível. O ajuste fica no canto do canvas, onde se usa.
  const [zoom, setZoom] = useState(0.85)
  const arrastando = useRef<{ id: string; dx: number; dy: number } | null>(null)
  const area = useRef<HTMLDivElement>(null)

  // Esc cancels a pending connection. Without it the only way out of the mode
  // is to complete a link the operator no longer wants.
  useEffect(() => {
    if (!ligando) return
    const aoTeclar = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setLigando(null)
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [ligando])

  // O retângulo medido é o do elemento JÁ ESCALADO: dividir pelo zoom converte
  // o pixel da tela de volta à coordenada do canvas — sem isso, arrastar com
  // zoom a 85% faz a caixa fugir do ponteiro.
  const aoMover = useCallback(
    (ev: React.PointerEvent) => {
      const a = arrastando.current
      if (!a || !area.current) return
      const caixa = area.current.getBoundingClientRect()
      const x = Math.max(0, Math.round(((ev.clientX - caixa.left) / zoom - a.dx) / GRADE) * GRADE)
      const y = Math.max(0, Math.round(((ev.clientY - caixa.top) / zoom - a.dy) / GRADE) * GRADE)
      onMover(a.id, x, y)
    },
    [onMover, zoom],
  )

  const limites = etapas.reduce(
    (acc, e) => ({
      x: Math.max(acc.x, e.posicao.x + LARGURA + 80),
      y: Math.max(acc.y, e.posicao.y + alturaDe(e) + 80),
    }),
    { x: 720, y: 480 },
  )

  const porId = new Map(etapas.map((e) => [e.id, e]))

  return (
    <div className="relative max-h-[720px] overflow-auto rounded-xl border border-[color:var(--color-borda)] bg-[color:var(--color-superficie)]">
      {/* -------------------------------------------------------------- zoom */}
      <div className="sticky top-2 left-2 z-20 inline-flex w-fit items-center gap-0.5 rounded-lg border border-[color:var(--color-borda)] bg-white/95 p-0.5 shadow-sm">
        <button
          type="button"
          title="Afastar"
          onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))}
          className="flex size-7 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
        >
          −
        </button>
        <button
          type="button"
          title="Voltar a 85%"
          onClick={() => setZoom(0.85)}
          className="w-12 rounded-md py-1 text-center text-[11px] text-slate-500 tabular hover:bg-slate-100"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          title="Aproximar"
          onClick={() => setZoom((z) => Math.min(1.25, Math.round((z + 0.1) * 10) / 10))}
          className="flex size-7 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
        >
          +
        </button>
      </div>
      {ligando && (
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-amber-200 bg-[color:var(--color-atencao-fundo)] px-4 py-2 text-[13px] text-amber-900">
          <span>
            Ligando o caminho <strong>{ROTULO_RAMO[ligando.quando]}</strong> — clique na etapa de
            destino.
          </span>
          <button type="button" className="underline" onClick={() => setLigando(null)}>
            cancelar (Esc)
          </button>
        </div>
      )}

      <div style={{ width: limites.x * zoom, height: limites.y * zoom, marginTop: -36 }}>
      <div
        ref={area}
        className="relative"
        style={{
          width: limites.x,
          height: limites.y,
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
          backgroundImage:
            'radial-gradient(circle, color-mix(in srgb, var(--color-borda) 70%, transparent) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }}
        onPointerMove={aoMover}
        onPointerUp={() => (arrastando.current = null)}
        onClick={(ev) => {
          if (ev.target === ev.currentTarget) {
            onSelecionar(null)
            setLigando(null)
          }
        }}
      >
        {/* ------------------------------------------------------------ arestas */}
        <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
          {etapas.flatMap((e) =>
            e.proximas.map((a) => {
              const destino = porId.get(a.para)
              if (!destino) return null
              const ramos = RAMOS[e.tipo]
              const i = Math.max(0, ramos.indexOf(a.quando))
              const largura = LARGURA / (ramos.length || 1)
              const x1 = e.posicao.x + largura * i + largura / 2
              const y1 = e.posicao.y + alturaDe(e)
              const x2 = destino.posicao.x + LARGURA / 2
              const y2 = destino.posicao.y

              return (
                <g key={`${e.id}-${a.quando}`} className="pointer-events-auto">
                  <path
                    d={caminho(x1, y1, x2, y2)}
                    fill="none"
                    stroke="var(--color-borda-forte, #94a3b8)"
                    strokeWidth={1.6}
                  />
                  {/* Alvo de clique generoso e invisível: a curva tem 1,6px e
                      ninguém acerta 1,6px com o mouse. */}
                  {!somenteLeitura && (
                    <path
                      d={caminho(x1, y1, x2, y2)}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={14}
                      className="cursor-pointer"
                      onClick={() => onDesconectar(e.id, a.quando)}
                    >
                      <title>Remover ligação</title>
                    </path>
                  )}
                  <circle cx={x2} cy={y2} r={3} fill="var(--color-borda-forte, #94a3b8)" />
                </g>
              )
            }),
          )}
        </svg>

        {/* ------------------------------------------------------------- etapas */}
        {etapas.map((e) => {
          const cor = CORES[e.tipo]
          const ramos = RAMOS[e.tipo]
          const problemas = problemasPorEtapa.get(e.id) ?? []
          const ativa = selecionada === e.id
          const alvoDeLigacao = ligando !== null && ligando.de !== e.id

          return (
            <div
              key={e.id}
              className={`absolute rounded-xl border-2 shadow-sm transition ${cor.borda} ${cor.fundo} ${
                ativa ? 'ring-2 ring-marca-500 ring-offset-2' : ''
              } ${problemas.length ? 'border-red-400' : ''} ${
                alvoDeLigacao ? 'cursor-copy ring-2 ring-amber-400' : ''
              }`}
              style={{ left: e.posicao.x, top: e.posicao.y, width: LARGURA }}
              onClick={() => {
                if (ligando && ligando.de !== e.id) {
                  onConectar(ligando.de, ligando.quando, e.id)
                  setLigando(null)
                } else {
                  onSelecionar(e.id)
                }
              }}
            >
              <button
                type="button"
                className={`flex w-full items-center gap-2 rounded-t-[10px] px-3 py-1.5 text-left text-[11px] font-semibold tracking-wide uppercase ${cor.texto} ${
                  somenteLeitura ? '' : 'cursor-grab active:cursor-grabbing'
                }`}
                onPointerDown={(ev) => {
                  if (somenteLeitura || ligando) return
                  const caixa = area.current?.getBoundingClientRect()
                  if (!caixa) return
                  arrastando.current = {
                    id: e.id,
                    dx: (ev.clientX - caixa.left) / zoom - e.posicao.x,
                    dy: (ev.clientY - caixa.top) / zoom - e.posicao.y,
                  }
                  ;(ev.target as HTMLElement).setPointerCapture(ev.pointerId)
                }}
                onPointerUp={() => (arrastando.current = null)}
              >
                {NOMES[e.tipo]}
                {problemas.length > 0 && (
                  <span
                    className="ml-auto flex size-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white"
                    title={problemas.join(' · ')}
                  >
                    !
                  </span>
                )}
              </button>

              <p className="px-3 pt-0.5 pb-2 text-[13px] leading-snug text-slate-700">
                {rotuloEtapa(e)}
              </p>

              {ramos.length > 0 && (
                <div className="flex border-t border-black/5">
                  {ramos.map((r) => {
                    const ligada = e.proximas.some((a) => a.quando === r)
                    return (
                      <button
                        key={r}
                        type="button"
                        disabled={somenteLeitura}
                        onClick={(ev) => {
                          ev.stopPropagation()
                          if (ligada) onDesconectar(e.id, r)
                          else setLigando({ de: e.id, quando: r })
                        }}
                        className={`flex-1 border-r border-black/5 py-1 text-[11px] font-medium last:border-r-0 disabled:opacity-60 ${
                          ligada
                            ? 'text-slate-500 hover:bg-black/5'
                            : 'text-marca-700 hover:bg-marca-100'
                        }`}
                        title={ligada ? 'Remover ligação' : 'Ligar a partir daqui'}
                      >
                        {ROTULO_RAMO[r]}
                        {ligada ? ' ✓' : ' +'}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
      </div>
    </div>
  )
}
